import { WetInkModalState } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/types';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useSubmitSignableForm } from '@dealstack/frontend/shared/modules/document/signing/hooks/useSubmitSignableForm';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	ACCEPT_FILES_TYPES,
	DropzoneV2,
} from '@dealstack/frontend/shared/components/DropzoneV2/DropzoneV2';
import { SelectedFileBox } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SelectedFileBox';
import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { Modal, Flex, Button, Text, Title, Divider, ActionIcon } from '@mantine/core';
import React, { useCallback } from 'react';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

interface Props {
	dealId: string;
	mipId: string;
	investorId: string;
	signableFormModal: WetInkModalState;
	setSignableFormModal: React.Dispatch<React.SetStateAction<WetInkModalState>>;
	handlePreviewFile: (
		fileId: string,
		fileName: string,
		documentWorkflowInstanceId: string | undefined,
		shouldPreview: boolean
	) => void;
	refetch: () => void;
}

export const SignableFormModal = ({
	dealId,
	mipId,
	investorId,
	signableFormModal,
	setSignableFormModal,
	handlePreviewFile,
	refetch,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const { handleSubmitForm, submitFormIsLoading } = useSubmitSignableForm({
		dealId,
		programmeId: mipId,
		documentWorkflowInstanceId: signableFormModal.contract?.documentWorkflowInstanceId ?? '',
	});

	const handleCloseSignableFormModal = useCallback(() => {
		setSignableFormModal({
			contract: null,
			opened: false,
			selectedFile: undefined,
			isSaving: false,
		});
	}, [setSignableFormModal]);

	const handleSignableFormDownload = useCallback(() => {
		if (!signableFormModal.contract) {
			return;
		}

		handlePreviewFile(
			signableFormModal.contract.fileId,
			signableFormModal.contract.fileName,
			signableFormModal.contract.documentWorkflowInstanceId,
			false
		);
	}, [signableFormModal.contract, handlePreviewFile]);

	const handleSignableFormFileSelect = useCallback(
		(file: File) => {
			setSignableFormModal((prev) => ({ ...prev, selectedFile: file }));
		},
		[setSignableFormModal]
	);

	const handleSignableFormFileRemove = useCallback(() => {
		setSignableFormModal((prev) => ({ ...prev, selectedFile: undefined }));
	}, [setSignableFormModal]);

	const handleSignableFormSave = useCallback(async () => {
		if (!signableFormModal.selectedFile || !signableFormModal.contract) {
			return;
		}

		setSignableFormModal((prev) => ({ ...prev, isSaving: true }));

		try {
			await handleSubmitForm({
				selectedFile: signableFormModal.selectedFile,
				investorId,
			});

			handleCloseSignableFormModal();
			refetch();
		} catch (error) {
			const message = getUserFacingErrorMessage(error);
			showToastV2({
				title: message || tInvestor('investment.contracts.signableFormModal.submit_error'),
				state: 'error',
			});
			logger.error('Error submitting signable form:', error);
		} finally {
			setSignableFormModal((prev) => ({ ...prev, isSaving: false }));
		}
	}, [
		signableFormModal.selectedFile,
		signableFormModal.contract,
		setSignableFormModal,
		handleSubmitForm,
		investorId,
		handleCloseSignableFormModal,
		refetch,
	]);

	if (!signableFormModal.opened) {
		return null;
	}

	const isSaving = signableFormModal.isSaving || submitFormIsLoading;
	const isSaveDisabled = !signableFormModal.selectedFile || isSaving;

	return (
		<Modal
			title={
				<Title order={5}>{tInvestor('investment.contracts.signableFormModal.title')}</Title>
			}
			opened={signableFormModal.opened}
			onClose={
				isSaving
					? () => {
							// skip the modal closing until done
						}
					: handleCloseSignableFormModal
			}
			closeOnClickOutside={!isSaving}
			closeOnEscape={!isSaving}
			size="lg"
			centered
		>
			{isSaving && <InlineLoader />}
			<Flex direction="column" gap="md">
				<Text size="sm" c="dimmed">
					{tInvestor('investment.contracts.signableFormModal.instructions')}
				</Text>

				<Divider />

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							1
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.contracts.signableFormModal.downloadForm')}
						</Title>
						<Text sx={{ maxWidth: '20rem' }}>
							{tInvestor(
								'investment.contracts.signableFormModal.downloadFormDescription'
							)}
						</Text>
					</Flex>
				</Flex>

				<Button
					variant="outline"
					leftSection={<Icon variant="download" size="md" />}
					onClick={handleSignableFormDownload}
				>
					{tInvestor('investment.contracts.signableFormModal.downloadButton')}
				</Button>

				<Divider />

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							2
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.contracts.signableFormModal.uploadForm')}
						</Title>
						<Text sx={{ maxWidth: '20rem' }}>
							{tInvestor(
								'investment.contracts.signableFormModal.uploadFormDescription'
							)}
						</Text>
					</Flex>
				</Flex>

				{signableFormModal.selectedFile ? (
					<SelectedFileBox>
						<Flex justify="space-between" align="center">
							<Flex gap="xs" align="center">
								<Icon variant="document-v3" size="lg" />
								<Text size="sm" fw={500}>
									{signableFormModal.selectedFile.name}
								</Text>
							</Flex>
							<ActionIcon
								variant="subtle"
								color="red"
								onClick={handleSignableFormFileRemove}
								aria-label="Remove file"
							>
								<Icon variant="trash-can" size="md" />
							</ActionIcon>
						</Flex>
					</SelectedFileBox>
				) : (
					<DropzoneV2
						onDropFile={handleSignableFormFileSelect}
						accept={ACCEPT_FILES_TYPES.pdf}
						title={tInvestor('investment.contracts.signableFormModal.drag_drop')}
						description={tInvestor(
							'investment.contracts.signableFormModal.only_pdf_accepted'
						)}
						buttonText={tInvestor(
							'investment.contracts.signableFormModal.browse_files'
						)}
					/>
				)}

				<Flex justify="flex-end" gap="sm">
					<Button
						variant="outline"
						onClick={handleCloseSignableFormModal}
						disabled={isSaving}
					>
						{tInvestor('investment.contracts.signableFormModal.cancel')}
					</Button>
					<Button
						onClick={handleSignableFormSave}
						disabled={isSaveDisabled}
						leftSection={<Icon variant="checkmark-circle" size="md" />}
					>
						{tInvestor('investment.contracts.signableFormModal.submit_sign')}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
};
